import React, { useEffect, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { AppRoutes } from '../interfaces/store/appStore';
import { useNavigate } from 'react-router-dom';

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

const BlackFridayCountdown: React.FC = () => {
  const navigate = useNavigate()
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [copied, setCopied] = useState<boolean>(false);

  useEffect(() => {
    const targetDate = new Date('2025-12-02T23:59:59-08:00');

    const updateCountdown = () => {
      const now = new Date();
      const difference = targetDate.getTime() - now.getTime();

      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);

        setTimeLeft({ days, hours, minutes, seconds });
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!copied) {
      return undefined;
    }

    const timeout = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timeout);
  }, [copied]);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText('BF25');
      setCopied(true);
    } catch (error) {
      setCopied(false);
    }
  };

  const formatTime = (value: number): string => value.toString().padStart(2, '0');

  return (
    <Container>
      <Overlay />
      <AnimatedBlob $position="left" />
      <AnimatedBlob $position="right" />

      <Content>
        <HeadlineWrapper>
          <Emoji>🎉</Emoji>
          <HeadlineText>
            <HeadlineTag>Black Friday Special</HeadlineTag>
            <HeadlineDescription>
              Get an extra <Highlight>30% OFF</Highlight> on top of the existing 30% discount on all yearly plans
            </HeadlineDescription>
          </HeadlineText>
        </HeadlineWrapper>

        <ActionsWrapper>
          <CtaColumn>
            <CtaButton onClick={() => navigate(AppRoutes.BILLING)}>
              Claim Offer
            </CtaButton>
            <CopyButton onClick={onCopy} title="Click to copy code" type="button" $copied={copied}>
              {copied ? '✓ Copied!' : (
                <>
                  Coupon: <CopyCode>BF25</CopyCode>
                </>
              )}
            </CopyButton>
          </CtaColumn>

          <CountdownContainer>
            <CountdownSegment>
              <CountdownValue>{formatTime(timeLeft.days)}</CountdownValue>
              <CountdownLabel>Days</CountdownLabel>
            </CountdownSegment>
            <CountdownSeparator>:</CountdownSeparator>
            <CountdownSegment>
              <CountdownValue>{formatTime(timeLeft.hours)}</CountdownValue>
              <CountdownLabel>Hours</CountdownLabel>
            </CountdownSegment>
            <CountdownSeparator>:</CountdownSeparator>
            <CountdownSegment>
              <CountdownValue>{formatTime(timeLeft.minutes)}</CountdownValue>
              <CountdownLabel>Min</CountdownLabel>
            </CountdownSegment>
            <CountdownSeparator>:</CountdownSeparator>
            <CountdownSegment>
              <CountdownValue $emphasized>{formatTime(timeLeft.seconds)}</CountdownValue>
              <CountdownLabel>Sec</CountdownLabel>
            </CountdownSegment>
          </CountdownContainer>
        </ActionsWrapper>
      </Content>
    </Container>
  );
};

const pulse = keyframes`
  0% {
    opacity: 0.4;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.05);
  }
  100% {
    opacity: 0.4;
    transform: scale(1);
  }
`;

const Container = styled.div`
  position: relative;
  overflow: hidden;
  padding: 10px 0;
  background: linear-gradient(135deg, #000000 0%, #1a1a2e 50%, #0f3460 100%);
  backdrop-filter: blur(10px);
  font-family: 'Nunito Sans', sans-serif;
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.1));
  backdrop-filter: blur(20px);
`;

const AnimatedBlob = styled.div<{ $position: 'left' | 'right' }>`
  position: absolute;
  width: 16rem;
  height: 16rem;
  border-radius: 9999px;
  filter: blur(60px);
  animation: ${pulse} 6s ease-in-out infinite;
  ${({ $position }) =>
    $position === 'left'
      ? `
        top: -2rem;
        left: 25%;
        background: rgba(168, 85, 247, 0.2);
      `
      : `
        bottom: -2rem;
        right: 25%;
        background: rgba(59, 130, 246, 0.2);
        animation-delay: 1s;
      `}
`;

const Content = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  max-width: 78rem;
  margin: 0 auto;
  color: #ffffff;

  @media (max-width: 1024px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const HeadlineWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
`;

const Emoji = styled.span`
  font-size: 2rem;
`;

const HeadlineText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const HeadlineTag = styled.div`
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.15rem;
  color: #facc15;
`;

const HeadlineDescription = styled.div`
  font-size: 1rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.95);
`;

const Highlight = styled.span`
  color: #facc15;
  font-weight: 700;
  font-size: 1.1rem;
`;

const ActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;

  @media (max-width: 1024px) {
    width: 100%;
    justify-content: space-between;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const CtaColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
`;

const CtaButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 700;
  color: #111827;
  background: linear-gradient(90deg, #facc15, #fb923c);
  border-radius: 9999px;
  border: none;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
  box-shadow: 0 10px 30px rgba(250, 204, 21, 0.35);
  cursor: pointer;

  &:hover {
    transform: scale(1.05);
    background: linear-gradient(90deg, #fde047, #fb923c);
  }
`;

const CopyButton = styled.button<{ $copied: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  padding: 0.375rem 1rem;
  font-size: 0.75rem;
  font-weight: 500;
  color: #ffffff;
  background: rgba(255, 255, 255, 0.1);
  border: 2px solid rgba(250, 204, 21, 0.5);
  border-radius: 0.75rem;
  backdrop-filter: blur(12px);
  cursor: pointer;
  transition: transform 0.2s ease, background 0.2s ease, border-color 0.2s ease;

  &:hover {
    transform: scale(1.05);
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(250, 204, 21, 0.85);
  }

  ${({ $copied }) =>
    $copied &&
    `
      color: #4ade80;
      border-color: rgba(74, 222, 128, 0.75);
    `}
`;

const CopyCode = styled.span`
  font-weight: 700;
  letter-spacing: 0.3rem;
  color: #facc15;
`;

const CountdownContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 1rem;
  backdrop-filter: blur(18px);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const CountdownSegment = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;
`;

const CountdownValue = styled.div<{ $emphasized?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1rem;
  font-family: 'IBM Plex Mono', monospace;
  font-size: 1.1rem;
  font-weight: 700;
  color: #111827;
  background: linear-gradient(135deg, #facc15, #fb923c);
  border-radius: 0.75rem;
  box-shadow: 0 10px 25px rgba(250, 204, 21, 0.4);
  ${({ $emphasized }) =>
    $emphasized &&
    css`
      animation: ${pulse} 1.5s ease-in-out infinite;
    `}
`;

const CountdownLabel = styled.div`
  font-size: 0.7rem;
  font-weight: 500;
  color: rgba(209, 213, 219, 0.9);
  text-transform: uppercase;
  letter-spacing: 0.1rem;
`;

const CountdownSeparator = styled.span`
  font-family: 'IBM Plex Mono', monospace;
  font-size: 1.25rem;
  font-weight: 700;
  color: #facc15;
  padding-bottom: 1.25rem;
`;

export default BlackFridayCountdown;